import { useCallback, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Button, DatePicker, Form, Input, Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuildingCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import classNames from "classnames";
import { Structures } from "../../api/Structure";
import { RegisterStructureDTO } from "../../types/Structure";
import { useToasts } from "../../contexts/toasts";
import { Gender } from "../../enums/Gender";
import GooglePlacesSelect from "../StructureData/GooglePlacesSelect";

// Trasforma "" -> undefined per i campi opzionali (il BE valida contactEmail/pec
// come e-mail: una stringa vuota fallirebbe la validazione).
const emptyToUndefined = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

export default function RegisterStructure() {
  const { showToast } = useToasts();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [addressValue, setAddressValue] = useState<string>("");

  // Autocompletamento indirizzo tramite Google Places: riempie indirizzo, città,
  // CAP e coordinate. La provincia va inserita manualmente (non restituita).
  const onAddressSelect = useCallback(
    (city: {
      name: string;
      lat: number;
      lng: number;
      postalCode: string;
      locality: string;
    }) => {
      setAddressValue(city.name);
      form.setFieldsValue({
        structure: {
          address: city.name,
          city: city.locality,
          cap: city.postalCode,
          latitude: city.lat?.toString(),
          longitude: city.lng?.toString(),
        },
      });
    },
    [form]
  );

  const onFinish = useCallback(
    (values: any) => {
      const body: RegisterStructureDTO = {
        email: values.email.trim(),
        password: values.password,
        owner: {
          name: values.owner.name.trim(),
          familyName: values.owner.familyName.trim(),
          gender: values.owner.gender,
          birthDate: values.owner.birthDate
            ? `${values.owner.birthDate.format("YYYY-MM-DD")}T00:00:00.000Z`
            : undefined,
        },
        structure: {
          name: values.structure.name.trim(),
          address: values.structure.address.trim(),
          cap: String(values.structure.cap).trim(),
          city: values.structure.city.trim(),
          province: values.structure.province.trim(),
          latitude: String(values.structure.latitude).trim(),
          longitude: String(values.structure.longitude).trim(),
          contactPhone: emptyToUndefined(values.structure.contactPhone),
          contactEmail: emptyToUndefined(values.structure.contactEmail),
        },
        billing: {
          name: values.billing.name.trim(),
          vatCode: values.billing.vatCode.trim(),
          pec: values.billing.pec.trim(),
          uniCode: values.billing.uniCode.trim().toUpperCase(),
        },
      };

      setLoading(true);
      Structures.registerStructure(body).then(
        () => {
          setLoading(false);
          showToast(
            "register-structure-success",
            "Struttura registrata correttamente",
            `${body.structure.name} è ora attiva sul circuito Get Move.`,
            "success"
          );
          form.resetFields();
          setAddressValue("");
        },
        (err) => {
          setLoading(false);
          const code = err?.response?.data?.message;
          const message =
            code === "auth/email-already-exists"
              ? "Esiste già un utente con questa e-mail."
              : code === "auth/invalid-password"
              ? "La password non è valida (minimo 6 caratteri)."
              : "Errore nella registrazione della struttura, per favore riprova.";
          showToast("register-structure-error", message, "", "danger");
        }
      );
    },
    [form, showToast]
  );

  return (
    <Container fluid="md" className="mt-5 mb-5">
      <Row className="mb-2 align-items-center">
        <Col>
          <h4 className="mb-0">Registra una nuova Struttura</h4>
        </Col>
      </Row>
      <hr />
      <p className="text-muted">
        Crea in un'unica operazione l'utenza del gestore (Host), la struttura e i
        relativi dati di fatturazione.
      </p>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row>
          <Col xs={12}>
            <h6 className="text-primary mt-3">Account gestore</h6>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="E-mail"
              name="email"
              rules={[
                { required: true, message: "Inserisci l'e-mail" },
                { type: "email", message: "Inserisci un'e-mail valida" },
              ]}
            >
              <Input placeholder="email@dominio.it" />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: "Inserisci la password" },
                { min: 6, message: "Almeno 6 caratteri" },
              ]}
            >
              <Input.Password placeholder="Password (min. 6 caratteri)" />
            </Form.Item>
          </Col>

          <Col xs={12}>
            <h6 className="text-primary mt-3">Proprietario</h6>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="Nome"
              name={["owner", "name"]}
              rules={[{ required: true, message: "Inserisci il nome" }]}
            >
              <Input placeholder="Nome..." />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="Cognome"
              name={["owner", "familyName"]}
              rules={[{ required: true, message: "Inserisci il cognome" }]}
            >
              <Input placeholder="Cognome..." />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Sesso" name={["owner", "gender"]}>
              <Select
                allowClear
                placeholder="Seleziona..."
                options={[
                  { label: "Uomo", value: Gender.Male },
                  { label: "Donna", value: Gender.Female },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Data di nascita" name={["owner", "birthDate"]}>
              <DatePicker
                className="w-100"
                format="DD/MM/YYYY"
                placeholder="Seleziona una data..."
              />
            </Form.Item>
          </Col>

          <Col xs={12}>
            <h6 className="text-primary mt-3">Struttura</h6>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="Nome struttura"
              name={["structure", "name"]}
              rules={[
                { required: true, message: "Inserisci il nome struttura" },
              ]}
            >
              <Input placeholder="Nome struttura..." />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item label="Cerca indirizzo (Google)">
              <GooglePlacesSelect
                placeholder="Cerca un indirizzo per compilare i campi..."
                value={addressValue}
                disabled={loading}
                onSelectCity={onAddressSelect}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="Indirizzo"
              name={["structure", "address"]}
              rules={[{ required: true, message: "Inserisci l'indirizzo" }]}
            >
              <Input placeholder="Indirizzo..." />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="Città"
              name={["structure", "city"]}
              rules={[{ required: true, message: "Inserisci la città" }]}
            >
              <Input placeholder="Città..." />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item
              label="CAP"
              name={["structure", "cap"]}
              rules={[{ required: true, message: "Inserisci il CAP" }]}
            >
              <Input placeholder="CAP..." />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item
              label="Provincia"
              name={["structure", "province"]}
              rules={[{ required: true, message: "Inserisci la provincia" }]}
            >
              <Input placeholder="Provincia..." />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="Latitudine"
              name={["structure", "latitude"]}
              rules={[
                { required: true, message: "Inserisci la latitudine" },
                {
                  validator: (_, value) =>
                    value === undefined ||
                    value === "" ||
                    (+value >= -90 && +value <= 90)
                      ? Promise.resolve()
                      : Promise.reject("Valore tra -90 e 90"),
                },
              ]}
            >
              <Input placeholder="Es. 37.397579" />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="Longitudine"
              name={["structure", "longitude"]}
              rules={[
                { required: true, message: "Inserisci la longitudine" },
                {
                  validator: (_, value) =>
                    value === undefined ||
                    value === "" ||
                    (+value >= -180 && +value <= 180)
                      ? Promise.resolve()
                      : Promise.reject("Valore tra -180 e 180"),
                },
              ]}
            >
              <Input placeholder="Es. 13.525179" />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="Telefono di contatto"
              name={["structure", "contactPhone"]}
              rules={[
                {
                  pattern: /^[+]?[0-9]{10,15}$/,
                  message: "Numero di telefono non valido",
                },
              ]}
            >
              <Input placeholder="+39..." />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="E-mail di contatto"
              name={["structure", "contactEmail"]}
              rules={[{ type: "email", message: "E-mail non valida" }]}
            >
              <Input placeholder="E-mail di contatto..." />
            </Form.Item>
          </Col>

          <Col xs={12}>
            <h6 className="text-primary mt-3">Dati di fatturazione</h6>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="Intestatario"
              name={["billing", "name"]}
              rules={[
                { required: true, message: "Inserisci l'intestatario" },
              ]}
            >
              <Input placeholder="Ragione sociale..." />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="Partita IVA"
              name={["billing", "vatCode"]}
              rules={[
                { required: true, message: "Inserisci la Partita IVA" },
                {
                  pattern: /^[0-9]{11}$/,
                  message: "Deve contenere 11 cifre numeriche",
                },
              ]}
            >
              <Input placeholder="11 cifre..." />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="PEC"
              name={["billing", "pec"]}
              rules={[
                { required: true, message: "Inserisci la PEC" },
                { type: "email", message: "PEC non valida" },
              ]}
            >
              <Input placeholder="pec@dominio.it" />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              label="Codice Univoco"
              name={["billing", "uniCode"]}
              rules={[
                { required: true, message: "Inserisci il Codice Univoco" },
                {
                  pattern: /^[A-Za-z0-9]{6,7}$/,
                  message: "6 o 7 caratteri alfanumerici",
                },
              ]}
            >
              <Input placeholder="Es. 0000000" />
            </Form.Item>
          </Col>
        </Row>

        <div className="mt-3">
          <button
            type="submit"
            disabled={loading}
            className={classNames("button-primary", {
              "button-disabled": loading,
            })}
          >
            {loading ? (
              <TailSpin color="#FFF" width={15} height={15} />
            ) : (
              <>
                <FontAwesomeIcon icon={faBuildingCircleCheck} />
                <span className="ms-2">Registra Struttura</span>
              </>
            )}
          </button>
        </div>
      </Form>
    </Container>
  );
}
